import { useEffect, useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";

interface ModalProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  drawer?: boolean;
  id?: string;
  dismissible?: boolean;
}

let openDialogs = 0;
let previousOverflow = "";

/** Native top-layer dialogs provide focus containment and an inert background. */
export function Modal({ title, onClose, children, footer, wide = false, drawer = false, id, dismissible = true }: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const titleId = useId();
  const backdropPress = useRef(false);

  useEffect(() => {
    const dialog = dialogRef.current!;
    const opener = document.activeElement;
    if (openDialogs++ === 0) {
      previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = "hidden";
    }
    dialog.showModal();
    const viewport = window.visualViewport;
    const updateViewport = () => {
      // Keyboard shrink should resize the dialog, not hide its footer. Ignore pinch
      // zoom: the browser must remain free to pan the enlarged page.
      if (viewport && viewport.scale === 1) {
        dialog.style.setProperty("--dialog-height", `${viewport.height}px`);
        dialog.style.setProperty("--dialog-top", `${viewport.offsetTop}px`);
      } else {
        dialog.style.removeProperty("--dialog-height");
        dialog.style.removeProperty("--dialog-top");
      }
    };
    updateViewport();
    viewport?.addEventListener("resize", updateViewport);
    viewport?.addEventListener("scroll", updateViewport);
    return () => {
      viewport?.removeEventListener("resize", updateViewport);
      viewport?.removeEventListener("scroll", updateViewport);
      dialog.close();
      if (--openDialogs === 0) document.documentElement.style.overflow = previousOverflow;
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus({ preventScroll: true });
    };
  }, []);

  const outside = (x: number, y: number) => {
    const rect = dialogRef.current!.getBoundingClientRect();
    return x < rect.left || x > rect.right || y < rect.top || y > rect.bottom;
  };
  return createPortal(
    <dialog
      ref={dialogRef} id={id} className={`modal${wide ? " modal--wide" : ""}${drawer ? " modal--drawer" : ""}`}
      aria-labelledby={titleId}
      onKeyDown={(event) => {
        if (event.key !== "Tab") return;
        const controls = Array.from(dialogRef.current!.querySelectorAll<HTMLElement>(
          'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), iframe, [tabindex]:not([tabindex="-1"])',
        )).filter((element) => element.tabIndex >= 0 && element.getClientRects().length > 0);
        const destination = event.shiftKey && document.activeElement === controls[0]
          ? controls.at(-1) : !event.shiftKey && document.activeElement === controls.at(-1) ? controls[0] : undefined;
        if (destination) { event.preventDefault(); destination.focus(); }
      }}
      onCancel={(event) => { event.preventDefault(); if (dismissible) closeRef.current(); }}
      onPointerDown={(event) => { backdropPress.current = outside(event.clientX, event.clientY); }}
      onClick={(event) => {
        if (dismissible && backdropPress.current && outside(event.clientX, event.clientY)) closeRef.current();
        backdropPress.current = false;
      }}
    >
      <header className="modal__header">
        <h2 className="modal__title" id={titleId}>{title}</h2>
        <button type="button" className="modal__close" aria-label="Close dialog" autoFocus disabled={!dismissible} onClick={onClose}>×</button>
      </header>
      <div className="modal__body">{children}</div>
      {footer ? <footer className="modal__footer">{footer}</footer> : null}
    </dialog>, document.body,
  );
}
